import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Assignment as InitiativeIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { useDeepSearch } from '../../hooks/useDeepSearch';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  id: string;
  type: 'contact' | 'initiative' | 'event' | 'task';
  name: string;
  description?: string;
  metadata?: any;
  relevanceScore?: number;
}

export function Search() {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [gptResponse, setGptResponse] = useState<string | null>(null);
  const [gptLoading, setGptLoading] = useState(false);
  const [gptError, setGptError] = useState<string | null>(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [filters, setFilters] = useState({
    contacts: true,
    initiatives: true,
    events: true,
    tasks: true,
  });
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [allInitiatives, setAllInitiatives] = useState<any[]>([]);
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [allTasks, setAllTasks] = useState<any[]>([]);
  const [realTimeResults, setRealTimeResults] = useState<SearchResult[]>([]);
  const [realTimeLoading, setRealTimeLoading] = useState(false);
  
  const db = useDatabase();
  const navigate = useNavigate();
  const { startDeepSearch, checkStatus, clearCurrentQuery } = useDeepSearch();

  const handleFilterChange = (type: keyof typeof filters) => {
    setFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  // Debounced real-time search
  const debouncedSearch = useCallback(
    (searchQuery: string) => {
      // Calculate relevance score for search results
      const calculateRelevanceScore = (result: SearchResult, searchQuery: string): number => {
        const query = searchQuery.toLowerCase();
        let score = 0;

        // Description match gets highest score
        if (result.description?.toLowerCase().includes(query)) {
          score += 15;
        }

        // Exact name match gets high score
        if (result.name.toLowerCase().includes(query)) {
          score += 10;
        }

        // Metadata matches
        if (result.metadata) {
          Object.values(result.metadata).forEach(value => {
            if (typeof value === 'string' && value.toLowerCase().includes(query)) {
              score += 3;
            }
          });
        }

        return score;
      };

      if (!searchQuery.trim()) {
        setRealTimeResults([]);
        setRealTimeLoading(false);
        return;
      }

      if (!filters.contacts && !filters.initiatives && !filters.events && !filters.tasks) {
        setRealTimeResults([]);
        setRealTimeLoading(false);
        return;
      }

      setRealTimeLoading(true);

      // Use setTimeout for debouncing instead of the debounce function
      const timeoutId = setTimeout(async () => {
        try {
          // Filter data based on user selections
          const filteredContacts = filters.contacts ? allContacts : [];
          const filteredInitiatives = filters.initiatives ? allInitiatives : [];
          const filteredEvents = filters.events ? allEvents : [];
          const filteredTasks = filters.tasks ? allTasks : [];

          // Perform search across all data types
          const results: SearchResult[] = [];

          // Search contacts - prioritize description/bio matches
          if (filters.contacts) {
            filteredContacts.forEach(contact => {
              const name = contact.name.toLowerCase();
              const email = (contact.email || '').toLowerCase();
              const phone = (contact.phone || '').toLowerCase();
              const description = (contact.description || '').toLowerCase();
              const industry = (contact.industry || '').toLowerCase();
              const skills = (contact.skills || []).join(' ').toLowerCase();
              const industries = (contact.industries || []).join(' ').toLowerCase();
              const tags = Object.keys(contact.tags || {}).join(' ').toLowerCase();

              const query = searchQuery.toLowerCase();
              
              // Check if query appears in any field
              const hasMatch = name.includes(query) || 
                             email.includes(query) || 
                             phone.includes(query) || 
                             description.includes(query) || 
                             industry.includes(query) || 
                             skills.includes(query) || 
                             industries.includes(query) || 
                             tags.includes(query);

              if (hasMatch) {
                results.push({
                  id: contact.id,
                  type: 'contact',
                  name: contact.name,
                  description: contact.description || contact.email || 'No description available',
                  metadata: { 
                    industry: contact.industry, 
                    skills: contact.skills,
                    email: contact.email,
                    phone: contact.phone
                  },
                });
              }
            });
          }

          // Search initiatives - prioritize description matches
          if (filters.initiatives) {
            filteredInitiatives.forEach(initiative => {
              const name = initiative.name.toLowerCase();
              const description = (initiative.description || '').toLowerCase();
              const tags = (initiative.tags || []).join(' ').toLowerCase();
              const notes = (initiative.notes || []).join(' ').toLowerCase();

              const query = searchQuery.toLowerCase();
              
              // Check if query appears in any field
              const hasMatch = name.includes(query) || 
                             description.includes(query) || 
                             tags.includes(query) || 
                             notes.includes(query);

              if (hasMatch) {
                results.push({
                  id: initiative.id,
                  type: 'initiative',
                  name: initiative.name,
                  description: initiative.description || 'No description available',
                  metadata: { 
                    status: initiative.status,
                    tags: initiative.tags,
                    notes: initiative.notes
                  },
                });
              }
            });
          }

          // Search events - prioritize description matches
          if (filters.events) {
            filteredEvents.forEach(event => {
              const title = event.title.toLowerCase();
              const description = (event.description || '').toLowerCase();
              const type = (event.metadata?.type || '').toLowerCase();
              const notes = (event.metadata?.notes || []).join(' ').toLowerCase();

              const query = searchQuery.toLowerCase();
              
              // Check if query appears in any field
              const hasMatch = title.includes(query) || 
                             description.includes(query) || 
                             type.includes(query) || 
                             notes.includes(query);

              if (hasMatch) {
                results.push({
                  id: event.id,
                  type: 'event',
                  name: event.title,
                  description: event.description || 'No description available',
                  metadata: { 
                    type: event.metadata?.type,
                    contact_id: event.contact_id,
                    notes: event.metadata?.notes
                  },
                });
              }
            });
          }

          // Search tasks - prioritize title and description matches
          if (filters.tasks) {
            filteredTasks.forEach(task => {
              const title = task.title.toLowerCase();
              const description = (task.description || '').toLowerCase();
              const status = (task.status || '').toLowerCase();
              const priority = (task.priority || '').toLowerCase();
              const tags = (task.tags || []).join(' ').toLowerCase();

              const query = searchQuery.toLowerCase();
              
              // Check if query appears in any field
              const hasMatch = title.includes(query) || 
                             description.includes(query) || 
                             status.includes(query) || 
                             priority.includes(query) || 
                             tags.includes(query);

              if (hasMatch) {
                results.push({
                  id: task.id,
                  type: 'task',
                  name: task.title,
                  description: task.description || `Status: ${task.status}, Priority: ${task.priority}`,
                  metadata: { 
                    status: task.status,
                    priority: task.priority,
                    due_date: task.due_date,
                    entity_type: task.entity_type,
                    entity_id: task.entity_id,
                    tags: task.tags
                  },
                });
              }
            });
          }

          // Sort results by relevance (improved scoring)
          results.sort((a, b) => {
            const aScore = calculateRelevanceScore(a, searchQuery);
            const bScore = calculateRelevanceScore(b, searchQuery);
            return bScore - aScore;
          });

          setRealTimeResults(results);
        } catch (err) {
          console.error('Real-time search error:', err);
        } finally {
          setRealTimeLoading(false);
        }
      }, 300);

      // Return cleanup function
      return () => clearTimeout(timeoutId);
    },
    [filters, allContacts, allInitiatives, allEvents, allTasks]
  );

  // Load all data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [contactsResult, initiativesResult, eventsResult, tasksResult] = await Promise.all([
          db.getContacts(),
          db.getInitiatives(),
          db.getEvents(),
          db.getTasks(),
        ]);

        if (contactsResult.error) throw contactsResult.error;
        if (initiativesResult.error) throw initiativesResult.error;
        if (eventsResult.error) throw eventsResult.error;
        if (tasksResult.error) throw tasksResult.error;

        setAllContacts(contactsResult.data || []);
        setAllInitiatives(initiativesResult.data || []);
        setAllEvents(eventsResult.data || []);
        setAllTasks(tasksResult.data || []);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };

    loadData();
  }, [db]);

  // Trigger real-time search when query changes
  useEffect(() => {
    const cleanup = debouncedSearch(query);
    return cleanup;
  }, [query, debouncedSearch]);

  // Cleanup status check interval on unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }
    if (!filters.contacts && !filters.initiatives && !filters.events && !filters.tasks) {
      setError('Please select at least one type to search');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);
    setGptResponse(null);
    setGptError(null);
    clearCurrentQuery();

    // Use the real-time results as the search results
    setSearchResults(realTimeResults);

    // Start deep search using background function
    setGptLoading(true);
    setGptResponse(null);
    setGptError(null);
    
    try {
      const queryData = await startDeepSearch({
        userQuery: query,
        contacts: allContacts,
        initiatives: allInitiatives,
        events: allEvents,
        tasks: allTasks,
        instructions: 'You are a helpful assistant that answers questions about contacts, initiatives, events, and tasks.'
      });

      if (queryData) {
        // Start polling for status updates
        const interval = setInterval(async () => {
          const status = await checkStatus(queryData.id);
          if (status && (status.status === 'completed' || status.status === 'failed')) {
            clearInterval(interval);
            setStatusCheckInterval(null);
            
            if (status.status === 'completed' && status.answer) {
              setGptResponse(status.answer);
            } else if (status.status === 'failed' && status.error) {
              setGptError(status.error);
            }
            setGptLoading(false);
            setLoading(false);
          }
        }, 2000); // Check every 2 seconds
        
        setStatusCheckInterval(interval);
      } else {
        setGptLoading(false);
        setLoading(false);
      }
    } catch (gptErr: any) {
      console.error('Deep Search Error:', gptErr);
      setGptError(gptErr?.message || 'Failed to start deep search');
      setGptLoading(false);
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'event' && result.metadata?.contact_id) {
      navigate(`/contacts/${result.metadata.contact_id}`);
    } else {
      navigate(`/${result.type}s/${result.id}`);
    }
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'contact':
        return <PersonIcon />;
      case 'initiative':
        return <InitiativeIcon />;
      case 'event':
        return <EventIcon />;
      case 'task':
        return <AssignmentIcon />;
      default:
        return <SearchIcon />;
    }
  };

  const getResultColor = (type: string) => {
    switch (type) {
      case 'contact':
        return 'primary';
      case 'initiative':
        return 'secondary';
      case 'event':
        return 'success';
      case 'task':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Function to parse response and convert markdown links to HTML
  const parseResponseWithLinks = (response: string) => {
    if (!response) return response;
    
    let parsedResponse = response;
    
    // Convert markdown links to HTML links
    // Pattern: [Entity Name](/entity-type/entity-id)
    const markdownLinkRegex = /\[([^\]]+)\]\(\/([^)]+)\)/g;
    parsedResponse = parsedResponse.replace(markdownLinkRegex, (match, entityName, url) => {
      return `<a href="/${url}" style="color: #1976d2; text-decoration: underline; cursor: pointer;">${entityName}</a>`;
    });
    
    return parsedResponse;
  };

  return (
    <DashboardLayout title="Search">
      <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 }, backgroundColor: 'background.default' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
            Search
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Search across contacts, initiatives, events, and tasks
          </Typography>
        </Box>

        {/* Filter Checkboxes */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.contacts}
                  onChange={() => handleFilterChange('contacts')}
                  color="primary"
                />
              }
              label="Contacts"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.initiatives}
                  onChange={() => handleFilterChange('initiatives')}
                  color="secondary"
                />
              }
              label="Initiatives"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.events}
                  onChange={() => handleFilterChange('events')}
                  color="success"
                />
              }
              label="Events"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.tasks}
                  onChange={() => handleFilterChange('tasks')}
                  color="warning"
                />
              }
              label="Tasks"
            />
          </FormGroup>
        </Paper>

        {/* Search Input */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              label="Search query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter search terms..."
              variant="outlined"
              disabled={loading}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Searching...' : 'Deep Search'}
            </Button>
          </Box>
        </Paper>

        {/* Real-time Search Results */}
        {query.trim() && !hasSearched && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Search Results
              </Typography>
              {realTimeLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Searching...
                  </Typography>
                </Box>
              )}
              <Chip 
                label={`${realTimeResults.length} result${realTimeResults.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            </Box>

            {realTimeResults.length === 0 && !realTimeLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No results found for "{query}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try different keywords or check your spelling
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {realTimeResults.map((result, idx) => (
                  <React.Fragment key={`${result.type}-${result.id}`}>
                    <ListItem
                      onClick={() => handleResultClick(result)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { 
                          backgroundColor: 'action.hover'
                        },
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemIcon>
                        <Box sx={{ color: `${getResultColor(result.type)}.main` }}>
                          {getResultIcon(result.type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {result.name}
                            </Typography>
                            <Chip
                              label={result.type}
                              size="small"
                              color={getResultColor(result.type) as any}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            {result.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {result.description}
                              </Typography>
                            )}
                            {result.metadata && Object.keys(result.metadata).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {Object.entries(result.metadata).map(([key, value]) => (
                                  <Chip
                                    key={key}
                                    label={`${key}: ${value}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < realTimeResults.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* ChatGPT Search Results (only shown after button click) */}
        {hasSearched && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Search Results
              </Typography>
              <Chip 
                label={`${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            </Box>

            {searchResults.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No results found for "{query}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try different keywords or check your spelling
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {searchResults.map((result, idx) => (
                  <React.Fragment key={`${result.type}-${result.id}`}>
                    <ListItem
                      onClick={() => handleResultClick(result)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { 
                          backgroundColor: 'action.hover'
                        },
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemIcon>
                        <Box sx={{ color: `${getResultColor(result.type)}.main` }}>
                          {getResultIcon(result.type)}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {result.name}
                            </Typography>
                            <Chip
                              label={result.type}
                              size="small"
                              color={getResultColor(result.type) as any}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            {result.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {result.description}
                              </Typography>
                            )}
                            {result.metadata && Object.keys(result.metadata).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {Object.entries(result.metadata).map(([key, value]) => (
                                  <Chip
                                    key={key}
                                    label={`${key}: ${value}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < searchResults.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* ChatGPT Response */}
        {hasSearched && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Catalyze AI Results
            </Typography>
            {gptLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Querying Catalyze AI...</Typography>
              </Box>
            )}
            {gptError && (
              <Alert severity="error" sx={{ mb: 2 }}>{gptError}</Alert>
            )}
            {gptResponse && (
              <Box 
                sx={{ 
                  whiteSpace: 'pre-line', 
                  fontFamily: 'monospace', 
                  p: 2, 
                  borderRadius: 1,
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.dark',
                    }
                  }
                }}
                dangerouslySetInnerHTML={{ 
                  __html: parseResponseWithLinks(gptResponse) 
                }}
              />
            )}
          </Paper>
        )}
      </Box>
    </DashboardLayout>
  );
} 